import { SearchIcon, Trash2Icon } from "lucide-react"
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react"
import { useTranslation } from "react-i18next"
import {
  useNavigate,
  useSearchParams,
} from "react-router-dom"

import { AppBreadcrumb } from "@/components/layout/app-breadcrumb"
import { Header } from "@/components/layout/header"
import { Button } from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { FRONTEND_ROUTES } from "@/constants"
import { useAuth } from "@/contexts/auth"
import {
  useAddRepicRetry,
  useRemoveRepicRetry,
  useRepicConfiguration,
  useUpsertRepicConfiguration,
  useWhiteLabels,
} from "@/hooks"
import { toast } from "@/hooks/use-toast"

const API_TO_UI_LAST_ACTION: Record<
  ApiLastAction,
  Exclude<UiFinalAction, "">
> = {
  PAYMENT_METHOD_CHANGE: "change_payment_method",
  CANCEL: "cancel",
}

const UI_TO_API_LAST_ACTION: Record<
  Exclude<UiFinalAction, "">,
  RepicLastAction
> = {
  change_payment_method: "PAYMENT_METHOD_CHANGE",
  cancel: "CANCEL",
}

const API_TO_UI_PAYMENT_METHOD: Record<
  ApiPaymentMethod,
  Exclude<UiPaymentMethod, "">
> = {
  CREDIT_CARD: "credit_card",
  PIX: "pix",
  BOLETO: "boleto",
}

const UI_TO_API_PAYMENT_METHOD: Record<
  Exclude<UiPaymentMethod, "">,
  RepicPaymentMethod
> = {
  credit_card: "CREDIT_CARD",
  pix: "PIX",
  boleto: "BOLETO",
}

const toRetryDaysRows = (
  retries: RepicRetryItem[] | undefined
): RetryDaysRow[] => {
  return (retries ?? []).map((r, index) => ({
    attemptNumber: r.retryCount ?? r.count ?? index + 1,
    days: r.days ?? r.day ?? 0,
  }))
}

const parseNonNegativeInteger = (
  raw: string
): number | null => {
  const value = Number(raw)
  if (!Number.isFinite(value)) return null
  if (!Number.isInteger(value)) return null
  if (value < 0) return null
  return value
}

export function RepicCenterPage() {
  const { t } = useTranslation()

  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  const { selectedWhiteLabel, setSelectedWhiteLabel } =
    useAuth()
  const { data: whiteLabelsData } = useWhiteLabels()

  const selectedWhiteLabelUuid =
    selectedWhiteLabel?.uuid ?? ""

  const selectedWhiteLabelIdParam =
    searchParams.get("whiteLabelId") ?? ""

  useEffect(() => {
    if (!selectedWhiteLabelIdParam) return
    const next = (whiteLabelsData ?? []).find(
      (w) => w.uuid === selectedWhiteLabelIdParam
    )
    if (next && selectedWhiteLabel?.uuid !== next.uuid) {
      setSelectedWhiteLabel(next)
    }
  }, [
    selectedWhiteLabel?.uuid,
    selectedWhiteLabelIdParam,
    setSelectedWhiteLabel,
    whiteLabelsData,
  ])

  const handleWhiteLabelChange = useCallback(
    (uuid: string) => {
      const next = whiteLabelsData?.find(
        (w) => w.uuid === uuid
      )
      if (next) {
        setSelectedWhiteLabel(next)

        const params = new URLSearchParams(
          searchParams.toString()
        )
        params.set("whiteLabelId", uuid)
        params.delete("refetch")
        navigate(`?${params.toString()}`, {
          replace: true,
        })
      }
    },
    [
      navigate,
      searchParams,
      setSelectedWhiteLabel,
      whiteLabelsData,
    ]
  )

  const isWhiteLabelValid = Boolean(selectedWhiteLabelUuid)

  const {
    data: repicConfiguration,
    refetch: refetchRepicConfiguration,
  } = useRepicConfiguration(isWhiteLabelValid)

  const handleSearch = useCallback(() => {
    if (!selectedWhiteLabelUuid) return
    refetchRepicConfiguration()
  }, [selectedWhiteLabelUuid, refetchRepicConfiguration])

  const upsertRepicConfigurationMutation =
    useUpsertRepicConfiguration()

  const addRepicRetryMutation = useAddRepicRetry()
  const removeRepicRetryMutation = useRemoveRepicRetry()

  const [daysToAdd, setDaysToAdd] = useState<string>("")
  const [retryDaysRowsOverride, setRetryDaysRowsOverride] =
    useState<RetryDaysRow[] | null>(null)
  const [finalActionOverride, setFinalActionOverride] =
    useState<UiFinalAction | null>(null)
  const [paymentMethodOverride, setPaymentMethodOverride] =
    useState<UiPaymentMethod | null>(null)

  const apiFinalActionValue = useMemo<UiFinalAction>(() => {
    const lastAction = repicConfiguration?.lastAction
    if (!lastAction) return ""
    return API_TO_UI_LAST_ACTION[lastAction] ?? ""
  }, [repicConfiguration?.lastAction])

  const apiPaymentMethodValue =
    useMemo<UiPaymentMethod>(() => {
      const method = repicConfiguration?.paymentMethod
      if (!method) return ""
      return API_TO_UI_PAYMENT_METHOD[method] ?? ""
    }, [repicConfiguration?.paymentMethod])

  const apiRetryDaysRowsValue = useMemo<RetryDaysRow[]>(
    () =>
      toRetryDaysRows(
        repicConfiguration?.retryConfigurations ??
          repicConfiguration?.retries
      ),
    [
      repicConfiguration?.retryConfigurations,
      repicConfiguration?.retries,
    ]
  )

  // const apiFinalAction = useMemo<UiFinalAction>(() => {
  //   const lastAction = repicConfiguration?.lastAction
  //   if (!lastAction) return ""
  //   return API_TO_UI_LAST_ACTION[lastAction] ?? ""
  // }, [repicConfiguration])

  // const apiPaymentMethod = useMemo<UiPaymentMethod>(() => {
  //   const method = repicConfiguration?.paymentMethod
  //   if (!method) return ""
  //   return API_TO_UI_PAYMENT_METHOD[method] ?? ""
  // }, [repicConfiguration])

  // const apiRetryDaysRows = useMemo<RetryDaysRow[]>(
  //   () =>
  //     toRetryDaysRows(
  //       repicConfiguration?.retryConfigurations ??
  //         repicConfiguration?.retries
  //     ),
  //   [repicConfiguration]
  // )

  const finalAction =
    finalActionOverride ?? apiFinalActionValue
  const paymentMethod =
    paymentMethodOverride ?? apiPaymentMethodValue
  const retryDaysRows = repicConfiguration?.id
    ? apiRetryDaysRowsValue
    : (retryDaysRowsOverride ?? apiRetryDaysRowsValue)

  const shouldShowRetriesCard = isWhiteLabelValid
  const isRetriesFormDisabled = !repicConfiguration

  const mappedLastAction = useMemo(() => {
    if (finalAction === "") return null
    return UI_TO_API_LAST_ACTION[finalAction]
  }, [finalAction])

  const mappedPaymentMethod = useMemo(() => {
    if (paymentMethod === "") return null
    return UI_TO_API_PAYMENT_METHOD[paymentMethod]
  }, [paymentMethod])

  const isSaveDisabled =
    upsertRepicConfigurationMutation.isPending ||
    !mappedLastAction ||
    !mappedPaymentMethod ||
    !isWhiteLabelValid

  const mappedRetryConfigurations = useMemo(() => {
    return retryDaysRows.map((row) => ({
      count: row.attemptNumber,
      day: row.days,
    }))
  }, [retryDaysRows])

  const retryConfigurationsPayload = useMemo(() => {
    if (!mappedRetryConfigurations.length) return undefined
    return mappedRetryConfigurations
  }, [mappedRetryConfigurations])

  const handleSave = useCallback(() => {
    if (!mappedLastAction || !mappedPaymentMethod) return

    upsertRepicConfigurationMutation.mutate({
      id: repicConfiguration?.id,
      lastAction: mappedLastAction,
      paymentMethod: mappedPaymentMethod,
      ...(retryConfigurationsPayload
        ? {
            retryConfigurations: retryConfigurationsPayload,
          }
        : {}),
    })
  }, [
    mappedLastAction,
    mappedPaymentMethod,
    retryConfigurationsPayload,
    repicConfiguration?.id,
    upsertRepicConfigurationMutation,
  ])

  // const mappedLastAction = useMemo(() => {
  //   if (finalAction === "") return null
  //   return UI_TO_API_LAST_ACTION[finalAction]
  // }, [finalAction])

  // const mappedPaymentMethod = useMemo(() => {
  //   if (paymentMethod === "") return null
  //   return UI_TO_API_PAYMENT_METHOD[paymentMethod]
  // }, [paymentMethod])

  // const isSaveDisabled =
  //   upsertRepicConfigurationMutation.isPending ||
  //   !mappedLastAction ||
  //   !mappedPaymentMethod

  // const handleSave = useCallback(() => {
  //   if (!mappedLastAction || !mappedPaymentMethod) return

  //   upsertRepicConfigurationMutation.mutate({
  //     id: repicConfiguration?.id,
  //     lastAction: mappedLastAction,
  //     paymentMethod: mappedPaymentMethod,
  //   })
  // }, [
  //   mappedLastAction,
  //   mappedPaymentMethod,
  //   repicConfiguration?.id,
  //   upsertRepicConfigurationMutation,
  // ])

  const parsedDaysToAdd = useMemo(
    () => parseNonNegativeInteger(daysToAdd),
    [daysToAdd]
  )

  const handleAddDays = () => {
    if (parsedDaysToAdd === null || parsedDaysToAdd <= 0) {
      toast({
        title: "Informe um valor de dias válido",
        variant: "destructive",
      })
      return
    }

    if (
      retryDaysRows.some(
        (row) => row.days === parsedDaysToAdd
      )
    ) {
      toast({
        title: "Este dia já foi adicionado",
        variant: "destructive",
      })
      return
    }

    if (repicConfiguration?.id) {
      if (addRepicRetryMutation.isPending) return
      addRepicRetryMutation.mutate({
        id: repicConfiguration.id,
        day: parsedDaysToAdd,
      })
      setDaysToAdd("")
      return
    }

    setRetryDaysRowsOverride((prev) => {
      const current = prev ?? retryDaysRows
      return [
        ...current,
        {
          attemptNumber: current.length + 1,
          days: parsedDaysToAdd,
        },
      ]
    })
    setDaysToAdd("")
  }

  const handleRemoveRetry = (count: number) => {
    if (!count) return

    if (repicConfiguration?.id) {
      if (removeRepicRetryMutation.isPending) return
      removeRepicRetryMutation.mutate({
        id: repicConfiguration.id,
        count,
      })
      return
    }

    setRetryDaysRowsOverride((prev) => {
      const current = prev ?? retryDaysRows
      const next = current.filter(
        (row) => row.attemptNumber !== count
      )
      return next.map((row, index) => ({
        ...row,
        attemptNumber: index + 1,
      }))
    })
  }

  const handleFinalActionChange = useCallback(
    (value: string) => {
      setFinalActionOverride(value as UiFinalAction)
    },
    []
  )

  const handlePaymentMethodChange = useCallback(
    (value: string) => {
      setPaymentMethodOverride(value as UiPaymentMethod)
    },
    []
  )

  return (
    <main className="flex w-full flex-1 flex-col">
      <Header>
        <AppBreadcrumb
          links={[
            {
              href: FRONTEND_ROUTES.DASHBOARD.href,
              label: t("sidebar.dashboard"),
            },
            {
              label: t("sidebar.repicCenter"),
            },
          ]}
        />
      </Header>
      <section className="flex flex-1 flex-col gap-4 overflow-x-hidden p-4 @container">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <h1 className="page-title">
            {t("pages.repicCenter.title")}
          </h1>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <div className="w-full sm:w-[260px]">
              <Select
                value={selectedWhiteLabelUuid}
                onValueChange={handleWhiteLabelChange}
              >
                <SelectTrigger id="repic-whitelabel-filter">
                  <SelectValue placeholder="Selecione uma loja" />
                </SelectTrigger>
                <SelectContent>
                  {(whiteLabelsData ?? [])
                    .filter((w) => Boolean(w.uuid))
                    .map((whitelabel) => (
                      <SelectItem
                        key={whitelabel.uuid}
                        value={whitelabel.uuid}
                      >
                        {whitelabel.name}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
            <Button
              type="button"
              onClick={handleSearch}
              disabled={!isWhiteLabelValid}
              className="btn-whitelabel"
            >
              <SearchIcon size={18} className="mr-2" />
              {t("common.search")}
            </Button>
          </div>
        </div>

        <div
          className={`grid grid-cols-1 gap-6 ${shouldShowRetriesCard ? "md:grid-cols-2" : ""}`}
        >
          <Card>
            <CardHeader>
              <CardTitle>Ação Final</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="repic-final-action">
                  Seleção
                </Label>
                <Select
                  value={finalAction}
                  onValueChange={handleFinalActionChange}
                >
                  <SelectTrigger id="repic-final-action">
                    <SelectValue placeholder="Selecione" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="change_payment_method">
                      Mudança do Meio de Pagamento
                    </SelectItem>
                    <SelectItem value="cancel">
                      Cancelamento
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="repic-payment-method">
                  Meio de Pagamento
                </Label>
                <Select
                  value={paymentMethod}
                  onValueChange={handlePaymentMethodChange}
                >
                  <SelectTrigger id="repic-payment-method">
                    <SelectValue placeholder="Selecione" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="boleto">
                      Boleto
                    </SelectItem>
                    <SelectItem value="credit_card">
                      Cartão de Crédito
                    </SelectItem>
                    <SelectItem value="pix">PIX</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {shouldShowRetriesCard && (
            <Card>
              <CardHeader>
                <CardTitle>Quantidade de Dias</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1fr_auto] sm:items-end">
                  <div className="space-y-1">
                    <Label htmlFor="repic-days">Dias</Label>
                    <Input
                      id="repic-days"
                      inputMode="numeric"
                      type="number"
                      value={daysToAdd}
                      disabled={isRetriesFormDisabled}
                      onChange={(e) =>
                        setDaysToAdd(e.target.value)
                      }
                    />
                  </div>
                  <Button
                    type="button"
                    onClick={handleAddDays}
                    disabled={
                      isRetriesFormDisabled ||
                      parsedDaysToAdd === null
                    }
                    className="w-full sm:w-auto"
                  >
                    Adicionar
                  </Button>
                </div>

                <div
                  className={`rounded-md border ${isRetriesFormDisabled ? "opacity-50" : ""}`}
                >
                  <Table divClassname="max-h-[420px]">
                    <TableHeader>
                      <TableRow>
                        <TableHead>
                          Nº Retentativas
                        </TableHead>
                        <TableHead>Dias</TableHead>
                        <TableHead className="w-[44px] p-0" />
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {retryDaysRows.map((row) => (
                        <TableRow key={row.attemptNumber}>
                          <TableCell>
                            {row.attemptNumber}
                          </TableCell>
                          <TableCell>{row.days}</TableCell>
                          <TableCell className="w-[44px] p-0 text-right">
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              disabled={
                                isRetriesFormDisabled ||
                                removeRepicRetryMutation.isPending
                              }
                              className="h-9 w-11"
                              onClick={() =>
                                handleRemoveRetry(
                                  row.attemptNumber
                                )
                              }
                            >
                              <Trash2Icon className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        <div className="flex justify-end">
          <Button
            type="button"
            onClick={handleSave}
            disabled={isSaveDisabled}
          >
            {repicConfiguration?.id
              ? "Atualizar"
              : "Salvar"}
          </Button>
        </div>
      </section>
    </main>
  )
}
